import { ApolloError, useQuery } from '@apollo/client'
import { useCallback, useEffect, useState } from 'react'
import { GET_BEST_PRACTICE_CLASHES } from 'api/graphQL/queries/bestPracticeClash.queries'
import { BestPracticeClash, ClashFilterStatus } from '../types'

interface UseClashListOptions {
    projectId: string | null
    treePath?: string | null
    filterStatus?: ClashFilterStatus
    skipQuery?: boolean
}

interface UseClashListResult {
    clashes: BestPracticeClash[]
    loading: boolean
    error: ApolloError | undefined
    handleClashResolved: (clashId: string) => void
    refetch: () => void
}

/**
 * Hook for managing clash list data fetching and filtering
 * Extracts data logic from ClashList component for better separation of concerns
 *
 * @param options - Configuration options for the hook
 * @returns Clash list data and handlers
 */
export const useClashList = ({
    projectId,
    treePath,
    filterStatus = 'active',
    skipQuery = false,
}: UseClashListOptions): UseClashListResult => {
    const [clashes, setClashes] = useState<BestPracticeClash[]>([])

    const { data, loading, error, refetch } = useQuery(GET_BEST_PRACTICE_CLASHES, {
        variables: {
            input: {
                projectId,
                includeResolved: true,
                treePath,
            },
        },
        skip: !projectId || skipQuery,
        fetchPolicy: 'network-only',
    })

    // Log error for debugging
    useEffect(() => {
        if (error) {
            console.error('Error fetching clashes:', error)
        }
    }, [error])

    // Apply local filtering based on filterStatus
    useEffect(() => {
        if (data?.bestPracticeClashes) {
            const allClashes = data.bestPracticeClashes as BestPracticeClash[]

            if (filterStatus === 'active') {
                setClashes(allClashes.filter((clash) => !clash.isResolved))
            } else {
                setClashes(allClashes.filter((clash) => clash.isResolved))
            }
        }
    }, [data, filterStatus])

    // Handle clash resolution with optimistic update for immediate UI feedback
    const handleClashResolved = useCallback(
        (clashId: string) => {
            // Optimistic update - immediately mark clash as resolved in local state
            setClashes((prevClashes) =>
                prevClashes.map((clash) =>
                    clash.id === clashId
                        ? {
                              ...clash,
                              isResolved: true,
                              resolvedDate: new Date().toISOString(),
                          }
                        : clash
                )
            )

            // Refetch to sync with server
            refetch()
        },
        [refetch]
    )

    return {
        clashes,
        loading,
        error,
        handleClashResolved,
        refetch,
    }
}
